import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getValidToken, API_URL } from './utils/authUtils';

const BACKEND_URL = API_URL;

const AdminBoardLiaisonManagement = () => {
  const navigate = useNavigate();
  const [boardLiaisons, setBoardLiaisons] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingLiaison, setEditingLiaison] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    company_name: '',
    title: '',
    phone: ''
  });

  useEffect(() => {
    fetchBoardLiaisons();
    fetchCompanies();
  }, []);

  const fetchBoardLiaisons = async () => {
    try {
      setLoading(true);
      const token = getValidToken();
      if (!token) {
        navigate('/admin');
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/board-liaisons`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setBoardLiaisons(response.data);
    } catch (err) {
      console.error('Error fetching board liaisons:', err);
      setError(err.response?.data?.detail || 'Failed to load board liaisons');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      setCompaniesLoading(true);
      const token = getValidToken();
      if (!token) {
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/companies`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setCompanies(response.data.companies || []);
    } catch (err) {
      console.error('Error fetching companies:', err);
      // Don't set error state here, just log it - form can still work with manual entry
    } finally {
      setCompaniesLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = getValidToken();
      if (!token) {
        navigate('/admin');
        return;
      }

      await axios.post(`${BACKEND_URL}/api/admin/board-liaisons`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setSuccess('Board liaison created successfully');
      setShowCreateForm(false);
      resetForm();
      fetchBoardLiaisons();
    } catch (err) {
      console.error('Error creating board liaison:', err);
      setError(err.response?.data?.detail || 'Failed to create board liaison');
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      email: '',
      password: '',
      company_name: '',
      title: '',
      phone: ''
    });
    setEditingLiaison(null);
  };

  const updatePermissions = async (liaisonId, permissions) => {
    try {
      const token = getValidToken();
      if (!token) {
        navigate('/admin');
        return;
      }

      await axios.put(`${BACKEND_URL}/api/admin/board-liaisons/${liaisonId}/permissions`, permissions, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setSuccess('Permissions updated successfully');
      fetchBoardLiaisons();
    } catch (err) {
      console.error('Error updating permissions:', err);
      setError(err.response?.data?.detail || 'Failed to update permissions');
    }
  };

  const handleToggleEnabled = (liaison) => {
    updatePermissions(liaison.id, {
      enabled: !liaison.permissions.enabled
    });
  };

  const handleToggleWriteAccess = (liaison) => {
    updatePermissions(liaison.id, {
      can_write: !liaison.permissions.can_write
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-300 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-300 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Board Liaison Management</h1>
        <p className="text-sm text-gray-600 mt-1">
          Manage board liaison accounts and their permissions
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800 text-sm">{error}</p>
          <button 
            onClick={() => setError('')}
            className="text-red-600 hover:text-red-800 text-sm underline ml-2"
          >
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-green-800 text-sm">{success}</p>
          <button 
            onClick={() => setSuccess('')}
            className="text-green-600 hover:text-green-800 text-sm underline ml-2"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Create Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
        >
          {showCreateForm ? 'Cancel' : 'Create New Board Liaison'}
        </button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="mb-6 bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-medium mb-4">Create New Board Liaison</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">
                Full Name *
              </label>
              <input
                type="text"
                name="full_name"
                id="full_name"
                required
                value={formData.full_name}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                id="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">
                Company Name *
              </label>
              <select
                name="company_name"
                id="company_name"
                required
                value={formData.company_name}
                onChange={handleInputChange}
                disabled={companiesLoading}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">
                  {companiesLoading ? 'Loading companies...' : 'Select a company'}
                </option>
                {companies.map((company) => (
                  <option key={company} value={company}>
                    {company}
                  </option>
                ))}
              </select>
              {companies.length === 0 && !companiesLoading && (
                <p className="mt-1 text-sm text-gray-500">
                  No companies found. Recruiters must be registered first.
                </p>
              )}
            </div>

            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Title
              </label>
              <input
                type="text"
                name="title"
                id="title"
                value={formData.title}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Phone
              </label>
              <input
                type="text"
                name="phone"
                id="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password *
              </label>
              <input
                type="password"
                name="password"
                id="password"
                required
                value={formData.password}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <button
                type="submit"
                className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md text-sm font-medium"
              >
                Create Board Liaison
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Board Liaisons List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium">Board Liaisons ({boardLiaisons.length})</h2>
        </div>
        
        {boardLiaisons.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No board liaisons found. Create one to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Liaison
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Permissions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {boardLiaisons.map((liaison) => (
                  <tr key={liaison.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {liaison.full_name}
                        </div>
                        <div className="text-sm text-gray-500">{liaison.email}</div>
                        {liaison.title && (
                          <div className="text-sm text-gray-500">{liaison.title}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{liaison.company_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        liaison.is_active && liaison.permissions.enabled
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {liaison.is_active && liaison.permissions.enabled ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        {liaison.permissions.can_write && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            Write Access
                          </span>
                        )}
                        {!liaison.permissions.enabled && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                            Disabled
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleToggleEnabled(liaison)}
                          className={`px-3 py-1 rounded text-xs font-medium ${
                            liaison.permissions.enabled
                              ? 'bg-red-100 text-red-800 hover:bg-red-200'
                              : 'bg-green-100 text-green-800 hover:bg-green-200'
                          }`}
                        >
                          {liaison.permissions.enabled ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => handleToggleWriteAccess(liaison)}
                          className={`px-3 py-1 rounded text-xs font-medium ${
                            liaison.permissions.can_write
                              ? 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                              : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                          }`}
                        >
                          {liaison.permissions.can_write ? 'Remove Write' : 'Grant Write'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBoardLiaisonManagement;